import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import SiteHeader from "../components/SiteHeader";

export default function AdminReceiptSignatures() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [form, setForm] = useState({
    id: null,
    label: "",
    is_active: false,
    file: null,
  });

  const [preview, setPreview] = useState("");

  const load = async () => {
    try {
      setErr("");
      setLoading(true);
      const { data } = await api.get("/api/admin/receipt-signatures");
      setItems(data.items || []);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load signatures");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => items, [items]);

  const onPick = (row) => {
    setForm({
      id: row.id,
      label: row.label || "",
      is_active: !!row.is_active,
      file: null,
    });
    setPreview(row.file_url || "");
  };

  const onReset = () => {
    setForm({ id: null, label: "", is_active: false, file: null });
    setPreview("");
  };

  const onFile = (e) => {
    const f = e.target.files?.[0] || null;
    setForm((p) => ({ ...p, file: f }));
    if (f) setPreview(URL.createObjectURL(f));
  };

  const onSave = async (e) => {
    e.preventDefault();
    setErr("");

    try {
      if (!form.id) {
        // create requires image
        if (!form.file) {
          setErr("Please choose signature image (PNG/JPG).");
          return;
        }
        const fd = new FormData();
        fd.append("image", form.file);
        fd.append("label", form.label || "");
        fd.append("is_active", form.is_active ? "1" : "0");

        await api.post("/api/admin/receipt-signatures", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        // update (image optional)
        const fd = new FormData();
        if (form.file) fd.append("image", form.file);
        fd.append("label", form.label || "");
        fd.append("is_active", form.is_active ? "1" : "0");

        await api.put(`/api/admin/receipt-signatures/${form.id}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      await load();
      onReset();
    } catch (e2) {
      setErr(e2?.response?.data?.message || "Save failed");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this signature?")) return;
    try {
      await api.delete(`/api/admin/receipt-signatures/${id}`);
      await load();
      if (form.id === id) onReset();
    } catch (e) {
      setErr(e?.response?.data?.message || "Delete failed");
    }
  };

  const styles = {
    wrap: { minHeight: "100vh", background: "#f1f5f9" },
    container: { maxWidth: 1100, margin: "0 auto", padding: "20px 14px" },
    headRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 },
    title: { fontSize: 22, fontWeight: 800, margin: 0, color: "#0f172a" },
    sub: { margin: "6px 0 0", color: "#64748b", fontSize: 13 },
    grid: { display: "grid", gridTemplateColumns: "1.2fr .8fr", gap: 14, marginTop: 14 },
    card: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 14, boxShadow: "0 10px 20px rgba(2,6,23,.06)" },
    input: { width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #cbd5e1", fontSize: 14 },
    label: { fontSize: 13, fontWeight: 700, marginBottom: 6, display: "block", color: "#0f172a" },
    row: { display: "flex", gap: 10, alignItems: "center" },
    btn: { border: 0, padding: "10px 14px", borderRadius: 999, cursor: "pointer", fontWeight: 700, fontSize: 14 },
    btnPrimary: { background: "#0f766e", color: "#fff" },
    btnGhost: { background: "#e2e8f0", color: "#0f172a" },
    badge: (active) => ({
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 800,
      background: active ? "#dcfce7" : "#e2e8f0",
      color: active ? "#166534" : "#0f172a",
    }),
    listItem: { padding: 12, borderRadius: 12, border: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", gap: 10, cursor: "pointer" },
    listTitle: { fontWeight: 800, color: "#0f172a" },
    small: { fontSize: 12, color: "#64748b" },
    danger: { background: "#fee2e2", color: "#7f1d1d" },
    previewBox: { border: "1px dashed #cbd5e1", borderRadius: 12, padding: 10, background: "#f8fafc", minHeight: 120, display: "grid", placeItems: "center" },
  };

  return (
    <div style={styles.wrap}>
      <SiteHeader title="Admin Dashboard" onLogout={() => navigate("/admin/login")} />

      <div style={styles.container}>
        <div style={styles.headRow}>
          <div>
            <h1 style={styles.title}>Receipt Signatures</h1>
            <p style={styles.sub}>Upload signature images and set one as Active.</p>
          </div>

          <button
            style={{ ...styles.btn, ...styles.btnGhost }}
            onClick={() => navigate("/dashboard")}
            type="button"
          >
            Back
          </button>
        </div>

        <div style={styles.grid}>
          <div style={styles.card}>
            <div style={styles.row}>
              <button style={{ ...styles.btn, ...styles.btnGhost }} onClick={load} type="button">
                Refresh
              </button>
            </div>

            {err && <div style={{ marginTop: 10, color: "#b91c1c", fontWeight: 700 }}>{err}</div>}
            {loading ? (
              <div style={{ marginTop: 12, color: "#64748b" }}>Loading...</div>
            ) : (
              <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
                {filtered.map((x) => (
                  <div key={x.id} style={styles.listItem} onClick={() => onPick(x)}>
                    <div>
                      <div style={styles.listTitle}>{x.label || "Signature"}</div>
                      <div style={styles.small}>ID: {x.id}</div>
                    </div>

                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span style={styles.badge(!!x.is_active)}>{x.is_active ? "ACTIVE" : "INACTIVE"}</span>
                      <button
                        type="button"
                        style={{ ...styles.btn, padding: "8px 12px", ...styles.danger }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(x.id);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}

                {!filtered.length && <div style={{ color: "#64748b" }}>No signatures found.</div>}
              </div>
            )}
          </div>

          <div style={styles.card}>
            <form onSubmit={onSave}>
              <label style={styles.label}>Label (optional)</label>
              <input
                style={styles.input}
                value={form.label}
                onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
                placeholder="Eg: Director Sign"
              />

              <div style={{ marginTop: 12 }}>
                <label style={styles.label}>Signature Image (PNG/JPG)</label>
                <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp" onChange={onFile} />
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>
                  Tip: upload transparent PNG for best look on receipt.
                </div>
              </div>

              <div style={{ marginTop: 12 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800 }}>
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                  />
                  Set as Active
                </label>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                  If active, all other signatures will be set inactive automatically.
                </div>
              </div>

              <div style={{ marginTop: 12 }}>
                <label style={styles.label}>Preview</label>
                <div style={styles.previewBox}>
                  {preview ? (
                    <img src={preview} alt="signature preview" style={{ maxWidth: 240, maxHeight: 120 }} />
                  ) : (
                    <div style={{ color: "#64748b" }}>No preview</div>
                  )}
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                <button style={{ ...styles.btn, ...styles.btnPrimary }} type="submit">
                  {form.id ? "Update" : "Upload"}
                </button>
                <button style={{ ...styles.btn, ...styles.btnGhost }} type="button" onClick={onReset}>
                  Clear
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
